// src/pages/BlogPost.jsx
import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FALLBACK_POSTS } from '../data/blogData';

function normalizePost(data, id) {
  return {
    id,
    slug: data.slug,
    title: data.title,
    excerpt: data.excerpt,
    category: data.category,
    img: data.imageUrl || data.img || '',
    date: data.date || (data.publishedAt?.toDate
      ? data.publishedAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : ''),
    content: data.content || '',
    metaTitle: data.metaTitle,
    metaDescription: data.metaDescription,
    metaKeywords: data.metaKeywords,
  };
}

// Parse markdown-ish content
function renderContent(text) {
  return text.trim().split('\n\n').map((block, i) => {
    const trimmed = block.trim();
    if (trimmed.startsWith('## ')) {
      return (
        <h2 key={i} className="title-4" style={{ margin: '48px 0 16px', color: 'var(--token-title)' }}>
          {trimmed.slice(3)}
        </h2>
      );
    }
    if (trimmed.startsWith('### ')) {
      return (
        <h3 key={i} className="title-5" style={{ margin: '32px 0 12px', color: 'var(--token-title)' }}>
          {trimmed.slice(4)}
        </h3>
      );
    }
    return (
      <p key={i} className="body-lg" style={{ color: 'var(--token-desc)', lineHeight: '1.75', marginBottom: '24px' }}>
        {trimmed}
      </p>
    );
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchPost() {
      try {
        // Try Firestore first
        const q = query(
          collection(db, 'blog_posts'),
          where('slug', '==', slug),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          setPost(normalizePost(d.data(), d.id));
        } else {
          // Fallback to static
          const found = FALLBACK_POSTS.find(p => p.slug === slug);
          if (found) setPost(found);
          else setNotFound(true);
        }
      } catch {
        const found = FALLBACK_POSTS.find(p => p.slug === slug);
        if (found) setPost(found);
        else setNotFound(true);
      }

      // Fetch all posts for "More insights" section
      try {
        const qAll = query(collection(db, 'blog_posts'), where('slug', '!=', slug));
        const snapAll = await getDocs(qAll);
        const fetched = snapAll.docs.map(d => normalizePost(d.data(), d.id)).slice(0, 2);
        setAllPosts(fetched.length > 0 ? fetched : FALLBACK_POSTS.filter(p => p.slug !== slug).slice(0, 2));
      } catch {
        setAllPosts(FALLBACK_POSTS.filter(p => p.slug !== slug).slice(0, 2));
      }

      setLoading(false);
    }
    fetchPost();
  }, [slug]);

  if (!loading && notFound) return <Navigate to="/blog" replace />;

  if (loading) {
    return (
      <>
        <Navbar variant="light" />
        <section style={{ padding: '128px 32px', textAlign: 'center', minHeight: '60vh' }}>
          <span className="spinner" style={{ borderColor: 'rgba(0,0,0,0.1)', borderTopColor: 'var(--primary-500)', margin: '0 auto', display: 'block' }} />
        </section>
        <Footer />
      </>
    );
  }

  if (!post) return null;

  return (
    <>
      <SEO
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        keywords={post.metaKeywords}
        url={`https://districtsourcingtech.com/blog/${post.slug}`}
        image={post.img}
        type="article"
      />

      <Navbar variant="light" />

      {/* Hero with full-width image overlay */}
      <section style={{ position: 'relative', height: '420px', overflow: 'hidden' }}>
        {post.img && (
          <img src={post.img} alt={post.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} loading="eager" />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '40px 32px' }}>
          <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
            <Link to="/blog" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '16px', textDecoration: 'none' }}>
              ← Back to Insights
            </Link>
            <p className="body-sm" style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '12px' }}>{post.category} · {post.date}</p>
            <h1 className="title-2" style={{ color: '#fff', maxWidth: '700px' }}>{post.title}</h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: '64px 32px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="title-4" style={{ marginBottom: '24px' }}>Introduction</h2>
          <p style={{ fontSize: '18px', lineHeight: '28px', color: 'var(--token-desc)', marginBottom: '40px' }}>{post.excerpt}</p>
          {post.content && renderContent(post.content)}
        </div>
      </section>

      {/* More posts */}
      {allPosts.length > 0 && (
        <section className="section section--alt">
          <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
            <h2 className="title-4" style={{ marginBottom: '40px' }}>More insights</h2>
            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              {allPosts.map(p => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="blog-card"
                  style={{ textDecoration: 'none', color: 'inherit', flex: 1, minWidth: '280px' }}
                >
                  {p.img && (
                    <div className="blog-card__image">
                      <img src={p.img} alt={p.title} loading="lazy" />
                    </div>
                  )}
                  <div>
                    <h3 className="title-5 blog-card__title">{p.title}</h3>
                    <p className="body-lg blog-card__desc" style={{ color: 'var(--token-desc)', marginTop: '8px' }}>{p.excerpt}</p>
                  </div>
                  <span className="read-more">Read more →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}